import type { FC, RefObject, Dispatch, SetStateAction } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearch } from "../../hooks/useSearch";
import { ensureInView } from "./utils";

import { cn } from "../../../assets/Utils/generic";
import GuestsModal from "./GuestsModal";

/**
 * Component to input and manage guest details for a search form.
 *
 * Props:
 * - setParams: Function to update search parameters.
 * - params: Current search parameters.
 * - mode: The display mode, either "mobile" or "desktop".
 *
 * Displays the number of adults and children, with options to increase or decrease the count.
 * Adapts its display based on the 'mode' prop.
 */
const GuestsInput: FC<{
  mode: "mobile" | "desktop";
  isGuestsOpen: boolean;
  setIsGuestsOpen: Dispatch<SetStateAction<boolean>>;
  setIsLocationOpen: Dispatch<SetStateAction<boolean>>;
  setIsCalendarOpen: Dispatch<SetStateAction<boolean>>;
  calendarInputRef: RefObject<HTMLDivElement>;
  locationInputRef: RefObject<HTMLDivElement>;
  parent: "home" | "nav" | "detail";
}> = ({
  mode,
  setIsGuestsOpen,
  isGuestsOpen,
  setIsLocationOpen,
  setIsCalendarOpen,
  calendarInputRef,
  locationInputRef,
  parent,
}) => {
  const { t } = useTranslation();
  const { searchParameters: params, setParameters: setParams } = useSearch();
  const [shouldOpenOver, setShouldOpenOver] = useState(false);

  const guestsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        guestsRef.current &&
        !guestsRef.current.contains(event.target as Node)
      ) {
        setIsGuestsOpen(false);

        (() => {
          const hasClickedOnLocationInput =
            locationInputRef.current &&
            locationInputRef.current.contains(event.target as Node);
          const hasClickedOnCalendarInput =
            calendarInputRef.current &&
            calendarInputRef.current.contains(event.target as Node);
          const hasClickedOnLocationOrCalendarInputs =
            hasClickedOnLocationInput || hasClickedOnCalendarInput;

          if (hasClickedOnLocationOrCalendarInputs) {
            return;
          }

          if (!params.destinationLabel && !params.tag) {
            setIsLocationOpen(true);
            return;
          }

          if (!params.arrivalDate || !params.departureDate) {
            setIsCalendarOpen(true);
          }
        })();
      }
    }

    if (isGuestsOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isGuestsOpen]);

  const handleScroll = () => {
    const input = document.getElementById("guests-input");
    const navH = document.querySelector("nav")?.clientHeight ?? 0;
    if (input) {
      const rect = input.getBoundingClientRect();
      // 100 px is an indicative height for guests input
      setShouldOpenOver(
        rect.bottom + 100 > window.innerHeight && rect.top - 100 > navH,
      );
    }
  };

  useEffect(() => {
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div
      className="relative"
      style={{
        padding: mode === "mobile" && parent !== "detail" ? "8px 24px" : "0",
        ...(parent !== "nav"
          ? {
              flexGrow: 1,
              flexBasis: 0,
              minWidth: 0,
            }
          : {}),
      }}
    >
      <div
        id="guests-input"
        className="cursor-pointer h100 w100"
        onClick={() => {
          setIsGuestsOpen(!isGuestsOpen);

          if (parent !== "nav" && guestsRef.current) {
            ensureInView(guestsRef);
          }
        }}
      >
        {parent === "detail" ? (
          <div className="flex flex-col w-full border border-[#E8E8E8] p-2 rounded-xl justify-center">
            <p className="text-xs leading-4 light dark-grey-color">
              {t("ds.guest_other")}
            </p>
            <p className="text-sm leading-4">
              {params.adults ?? t("ds.guests_ph")} {t("ds.adults")}{" "}
              {params.childrenAge.length > 0 && (
                <>
                  - {params.childrenAge.length} {t("ds.childs")}
                </>
              )}
            </p>
          </div>
        ) : (
          <>
            {/* Mobile */}
            <div className="d-flex flex-column gap-1 w100 d-md-none">
              {parent !== "nav" ? (
                <span className={"fs-body-sm dark-grey-color"}>
                  {t("ds.guests")}
                </span>
              ) : null}
              <span
                style={{
                  fontSize: mode === "mobile" ? "16px !important" : "",
                }}
                className={`${
                  mode === "desktop" ? "fs-body-xs" : "fs-body"
                } medium text-nowrap text-truncate`}
              >
                {params.adults ?? t("ds.guests_ph")} {t("ds.adults")}{" "}
                {params.childrenAge.length > 0 && (
                  <>
                    - {params.childrenAge.length} {t("ds.childs")}
                  </>
                )}
              </span>
            </div>
            {/* Desktop */}
            <div
              data-focused={isGuestsOpen}
              className={cn(
                "gap-2 w100 d-none d-md-flex align-items-center lg:py-2 px-3 rounded-2xl data-[focused=true]:bg-[#F8F8F8] hover:bg-[#F8F8F8] transition-colors duration-300",
                parent === "nav" && "data-[focused=true]:bg-transparent",
              )}
            >
              <div className="d-flex flex-column gap-1">
                {parent !== "nav" && (
                  <span
                    className={`${
                      mode === "desktop" ? "fs-body-sm" : "fs-body"
                    } light dark-grey-color text-nowrap`}
                  >
                    {t("ds.guests")}
                  </span>
                )}
                <span
                  style={{
                    fontSize: mode === "mobile" ? "16px !important" : "",
                  }}
                  className={cn(
                    "medium text-nowrap text-truncate",
                    mode === "desktop" ? "fs-body" : "fs-body",
                    parent === "nav" && "text-sm",
                  )}
                >
                  {params.adults ?? t("ds.guests_ph")} {t("ds.adults")}{" "}
                  {params.childrenAge.length > 0 && (
                    <>
                      - {params.childrenAge.length} {t("ds.childs")}
                    </>
                  )}
                </span>
              </div>
            </div>
          </>
        )}
      </div>

      <GuestsModal
        isGuestsOpen={isGuestsOpen}
        guestsRef={guestsRef}
        mode={mode}
        shouldOpenOver={shouldOpenOver}
        setIsGuestsOpen={setIsGuestsOpen}
        setIsLocationOpen={setIsLocationOpen}
        isNav={parent === "nav"}
        parent={parent}
      />
    </div>
  );
};

export default GuestsInput;
